import React, { createContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { jwtDecode } from 'jwt-decode';

import { AuthStateProps } from '../Types/Auth.type';
import {
   handleLogin,
   handleLogout,
   setPermissions,
   setRoles,
} from '../store/reducers/auth/AuthReducer';
import { apiAdapter } from '../config/adapters/api.adapter';
import { LoginResponse } from '../infrastructure/interfaces/login.response';
import { LoginMapper } from '../infrastructure/mappers/login.mapper';
import { RegisterResponse } from '../infrastructure/interfaces/register.response';
import Loader from '../Layout/Loader';

interface JWTContextType extends AuthStateProps {
   login: (email: string, password: string) => Promise<void>;
   register: (
      name: string,
      lastName: string,
      email: string,
      phone: string,
      password: string
   ) => Promise<void>;
   logout: () => void;
   forgotPassword: (email: string) => Promise<void>;
}

interface DecodedToken {
   exp: number;
   [key: string]: any;
}

const verifyToken = (serviceToken: string) => {
   if (!serviceToken) return false;

   try {
      const decoded: DecodedToken = jwtDecode(serviceToken);
      return decoded.exp > Math.floor(Date.now() / 1000);
   } catch (err) {
      console.error('Error al decodificar el token:', err);
      return false;
   }
};

const AuthContext = createContext<JWTContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
   const state = useSelector((state: any) => state.auth);
   const dispatch = useDispatch();

   useEffect(() => {
      const init = async () => {
         try {
            const serviceToken = window.localStorage.getItem('serviceToken');
            if (serviceToken && verifyToken(serviceToken)) {
               apiAdapter.setSession(serviceToken);
               const response = await apiAdapter.get<LoginResponse>('/auth/me');
               const user = LoginMapper.mapResponseToUser(response);
               dispatch(setRoles(LoginMapper.mapResponseToRoles(response)));
               dispatch(
                  setPermissions(LoginMapper.mapResponseToPermissions(response))
               );
               dispatch(handleLogin(user));
            } else {
               dispatch(handleLogout());
            }
         } catch (err) {
            console.error(err);
            dispatch(handleLogout());
         }
      };
      init();
   }, []);

   const login = async (email: string, password: string): Promise<void> => {
      const response = await apiAdapter.post<LoginResponse>('auth/login', {
         email,
         password,
      });
      const userToken = LoginMapper.mapResponseToToken(response);

      apiAdapter.setSession(userToken.accessToken);
      dispatch(setRoles(LoginMapper.mapResponseToRoles(response)));
      dispatch(setPermissions(LoginMapper.mapResponseToPermissions(response)));
      dispatch(handleLogin(userToken.user));
   };

   const register = async (
      name: string,
      lastName: string,
      email: string,
      phone: string,
      password: string
   ): Promise<void> => {
      await apiAdapter.post<RegisterResponse>('auth/register', {
         name,
         last_name: lastName,
         email,
         phone,
         password,
      });
   };

   const logout = async () => {
      apiAdapter.setSession('');
      dispatch(handleLogout());
   };

   const forgotPassword = async (email: string) => {
      await apiAdapter.post('auth/forgot-password', {
         email,
      });
   };

   if (state.isInitialized !== undefined && !state.isInitialized) {
      return <Loader />;
   }

   return (
      <AuthContext.Provider
         value={{ ...state, login, register, logout, forgotPassword }}
      >
         {children}
      </AuthContext.Provider>
   );
};

export default AuthContext;
